import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchCommissions } from '../../../services/marketing';
import CustomPagination from '../../../components/common/CustomPagination.js';

const Commissions = () => {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    marketing_manager: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchCommissions(params);
      
      if (response.results) {
        setCommissions(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setCommissions(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch commissions');
      setCommissions([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return commissions.map((commission, index) => ({
      ...commission,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [commissions, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: 'S/N',
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'order_number',
      headerName: 'Order Number',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.row.order?.order_number || 'N/A'
    },
    {
      field: 'marketing_manager',
      headerName: 'Marketing Manager',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const manager = params.row.marketing_manager;
        return manager ? `${manager.full_name || manager.employee_id || 'N/A'}` : 'N/A';
      }
    },
    {
      field: 'customer',
      headerName: 'Customer',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const customer = params.row.customer;
        return customer ? `${customer.username || customer.email || 'N/A'}` : 'N/A';
      }
    },
    {
      field: 'profit_rmb',
      headerName: 'Profit (RMB)',
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const value = params.value || 0;
        return `¥${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      field: 'commission_percentage',
      headerName: 'Commission %',
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const value = params.value || 0;
        return `${parseFloat(value).toFixed(2)}%`;
      }
    },
    {
      field: 'commission_amount_rmb',
      headerName: 'Commission (RMB)',
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const value = params.value || 0;
        return `¥${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      field: 'commission_amount_naira',
      headerName: 'Commission (NGN)',
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const value = params.value || 0;
        return `₦${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const status = params.value || 'pending';
        const colorMap = {
          'pending': 'warning',
          'approved': 'info',
          'paid': 'success',
          'cancelled': 'error'
        };
        return (
          <Chip
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            color={colorMap[status] || 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'calculated_at',
      headerName: 'Calculated At',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    }
  ], []);

  if (loading && commissions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          Commissions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('buttons.refresh')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Search"
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by order number, manager..."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Status"
                fullWidth
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
};

export default Commissions;
